import './oligo-config-preference.scss';

import * as Model from '../../models';
import * as ModelUtil from '../../models/util';
import { AddOligoRequest, ProjectService } from '../../service/project-service';

// Opportunity Improvement: If we can determine the minimum
// number of base pairs that will pass all validation checks
// and deduce that, that would be awesome. For now,
// we know that a 20bp sequence is the common minimum
const PREFERENCE_OLIGO_SEQUENCE: string = 'A'.repeat(20);

type ScopeWatcher = () => void;

type DropDownSelectionData = {
  definition?: Model.SelectionClientParamDef;
  items: Model.DropdownObj;
};

export type OligoConfigPreferenceOptions = {
  scale: DropDownSelectionData;
  purification: DropDownSelectionData;
  format: DropDownSelectionData;
};

export type OligoConfigPreferenceChangeHandler = {
  onOptionChangeStart(): void;
  onOptionChangeSuccess(newOptions: OligoConfigPreferenceOptions): void;
  onOptionChangeFailed(error: object): void;
};

type OptionRetriever = (request: AddOligoRequest) => ng.IPromise<OligoConfigPreferenceOptions>;

function acquireOptions($q: ng.IQService, projectService: ProjectService): OptionRetriever {
  return (request: AddOligoRequest) => {
    const deferred: ng.IDeferred<OligoConfigPreferenceOptions> = $q.defer();
    projectService.newOligoItem(request).then((oligo: Model.OligoItem) => {
      const { parameter } = oligo;
      parameter.basic.oligoValue.value = PREFERENCE_OLIGO_SEQUENCE;
      projectService.oligoParameterDefinition(parameter).then((paramDefs: Model.OligoParamDefs) => {
        const { scale, purification, format } = paramDefs.basic;
        const options: OligoConfigPreferenceOptions = {
          scale: {
            definition: scale,
            items: ModelUtil.selectionClientParamDefToDropDownContent(scale),
          },
          purification: {
            definition: purification,
            items: ModelUtil.selectionClientParamDefToDropDownContent(purification),
          },
          format: {
            definition: format,
            items: ModelUtil.selectionClientParamDefToDropDownContent(format),
          },
        };
        deferred.resolve(options);
      }, deferred.reject);
    }, deferred.reject);
    return deferred.promise;
  };
}

export function cloneOptions(options: OligoConfigPreferenceOptions): OligoConfigPreferenceOptions {
  const { scale, format, purification } = options;
  const scaleDef: Model.SelectionClientParamDef | undefined = scale.definition ?
    ModelUtil.deepClone(scale.definition as Model.SelectionClientParamDef) : scale.definition;
  const formatDef: Model.SelectionClientParamDef | undefined = format.definition ?
    ModelUtil.deepClone(format.definition as Model.SelectionClientParamDef) : format.definition;
  const purificationDef: Model.SelectionClientParamDef | undefined = purification.definition ?
    ModelUtil.deepClone(purification.definition as Model.SelectionClientParamDef) : purification.definition;
  const newOptions: OligoConfigPreferenceOptions = {
    scale: {
      definition: scaleDef,
      items: scaleDef ? ModelUtil.selectionClientParamDefToDropDownContent(scaleDef) : ModelUtil.defaultDropDownContent(),
    },
    purification: {
      definition: purificationDef,
      items: purificationDef ? ModelUtil.selectionClientParamDefToDropDownContent(purificationDef) : ModelUtil.defaultDropDownContent(),
    },
    format: {
      definition: formatDef,
      items: formatDef ? ModelUtil.selectionClientParamDefToDropDownContent(formatDef) : ModelUtil.defaultDropDownContent(),
    },
  };
  return newOptions;
}

export function preferenceOptionsToPreference(options: OligoConfigPreferenceOptions): Model.ProjectPreference {
  const { scale, purification, format } = options;
  const scaleDef = scale.definition as Model.SelectionClientParamDef;
  const purificationDef = purification.definition as Model.SelectionClientParamDef;
  const formatDef = format.definition as Model.SelectionClientParamDef;
  const preference: Model.ProjectPreference = {
    scale: { id: scaleDef.selected.id, value: scaleDef.selected.value },
    purification: { id: purificationDef.selected.id, value: purificationDef.selected.value },
    format: { id: formatDef.selected.id, value: formatDef.selected.value },
    isDefault: false,
  };
  return preference;
}

type OligoConfigPreferenceProperties = {
  preference?: Model.ProjectPreference;
  changeHandler?: OligoConfigPreferenceChangeHandler;
  fromBulk?: boolean;
};

export class OligoConfigPreferenceController implements ng.IController, OligoConfigPreferenceProperties {

  public preference?: Model.ProjectPreference;
  public changeHandler?: OligoConfigPreferenceChangeHandler;
  public fromBulk?: boolean;
  public options: OligoConfigPreferenceOptions | null = {
    scale: {
      items: ModelUtil.defaultDropDownContent(),
    },
    purification: {
      items: ModelUtil.defaultDropDownContent(),
    },
    format: {
      items: ModelUtil.defaultDropDownContent(),
    },
  };

  public loading: boolean = false;

  constructor($scope: ng.IScope, $q: ng.IQService, projectService: ProjectService) {
    const getOptionsDef: OptionRetriever = acquireOptions($q, projectService);
    const unwatchPreference = $scope.$watch(() => this.preference, (preference?: Model.ProjectPreference) => {
      this.loading = true;
      if (this.changeHandler) {
        this.changeHandler.onOptionChangeStart();
      }
      getOptionsDef({ preference, id: '0', index: 0 }).then((options: OligoConfigPreferenceOptions) => {
        this.options = options;
        if (this.changeHandler) {
          this.changeHandler.onOptionChangeSuccess(options);
        }
      }, (errorCause: object) => {
        if (this.changeHandler) {
          this.changeHandler.onOptionChangeFailed(errorCause);
        }
      }).finally(() => this.loading = false);
    });

    let selectionScopeWatchers: ScopeWatcher[] = [];
    const unwatchOptions = $scope.$watch(() => this.options, (options: OligoConfigPreferenceOptions | null) => {
      if (!options) {
        return;
      }
      let latestOptions: OligoConfigPreferenceOptions = cloneOptions(options);
      // clean up previous watchers on every change
      selectionScopeWatchers.forEach((unwatch: ScopeWatcher) => unwatch());
      selectionScopeWatchers.length = 0;
      const selectionWatchers: ScopeWatcher[] = [
        options.scale,
        options.purification,
        options.format,
      ].filter((dropdownData: DropDownSelectionData) => dropdownData.definition && typeof dropdownData.definition === 'object')
        .map((dropdownData: DropDownSelectionData) => {
          return ((selectionData: DropDownSelectionData) => {
            return $scope.$watch(() => selectionData.items.selected, (selected: Model.DropdownContentObj, oldSelected: Model.DropdownContentObj) => {
              if (selected === oldSelected || !selectionData.definition) {
                return;
              }
              const selectedParamDef: Model.SkuClientParamDef<string> = selectionData.definition.value
                .find((paramDef: Model.SkuClientParamDef<string>) =>
                  paramDef.name === selected.title) as Model.SkuClientParamDef<string>;
              selectionData.definition.selected = selectedParamDef;
              const updatedPreference: Model.ProjectPreference = preferenceOptionsToPreference(options);
              this.loading = true;
              if (this.changeHandler) {
                this.changeHandler.onOptionChangeStart();
              }
              getOptionsDef({ id: '0', index: 0, preference: updatedPreference }).then((newOptions: OligoConfigPreferenceOptions) => {
                this.options = newOptions;
                latestOptions = cloneOptions(newOptions);
                if (this.changeHandler) {
                  this.changeHandler.onOptionChangeSuccess(newOptions);
                }
              }, (errorCause: object) => {
                this.options = cloneOptions(latestOptions);
                if (this.changeHandler) {
                  this.changeHandler.onOptionChangeFailed(errorCause);
                }
              }).finally(() => this.loading = false);
            });
          })(dropdownData);
        });
      selectionScopeWatchers = selectionScopeWatchers.concat(selectionWatchers);
    });

    $scope.$on('$destroy', () => {
      selectionScopeWatchers.forEach((unwatch: ScopeWatcher) => unwatch());
      selectionScopeWatchers.length = 0;
      unwatchPreference();
      unwatchOptions();
    });
  }

}

export const OLIGO_CONFIG_PREFERENCE_NAME: string = 'oligoConfigPreference';
export const OligoConfigPreference: ng.IDirectiveFactory = () => {
  return {
    template: require('./oligo-config-preference.html'),
    controller: OligoConfigPreferenceController,
    controllerAs: 'oligoConfigPreference',
    restrict: 'E',
    scope: {
      preference: '<',
      changeHandler: '<',
      fromBulk: '<',
    },
    bindToController: true,
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/components/oligo-config-preference/oligo-config-preference.ts